// src/pages/invoices/components/NotaAjusteDialog.js
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, IconButton, TextField, Box, Typography, Table, TableBody,
  TableCell, TableHead, TableRow, FormControl, InputLabel, Select,
  MenuItem, Checkbox, FormControlLabel, CircularProgress, Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { formatearMoneda } from '../../../utils/currency';

/**
 * Emisión de una nota de crédito o débito sobre una factura.
 *
 * La nota ajusta renglones concretos, no un monto suelto: por eso el diálogo
 * arranca de los ítems de la factura y sólo deja elegir cuánto de cada uno se
 * ajusta. Una devolución de dos unidades de cinco es una nota por dos, con el
 * mismo precio y la misma alícuota que se facturaron — que el servidor hereda
 * del ítem original.
 *
 * Sin esto, la única salida ante una devolución parcial era anular la factura
 * entera y rehacerla.
 */
export const NotaAjusteDialog = ({ open, invoice, onClose, onEmitir }) => {
  const [tipo, setTipo] = useState('credit_note');
  const [motivo, setMotivo] = useState('');
  const [cantidades, setCantidades] = useState({});
  const [restaurarStock, setRestaurarStock] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);
  const [error, setError] = useState(null);

  const items = invoice?.items || [];

  useEffect(() => {
    if (!open) return;
    setTipo('credit_note');
    setMotivo('');
    setError(null);
    setRestaurarStock(true);
    // Arranca en cero: quien emite la nota elige qué ajustar, no al revés.
    setCantidades({});
  }, [open, invoice]);

  const idDe = (item, i) => String(item.product?._id || item.product || i);
  const precioDe = (item) => Number(item.price ?? item.precioUnitario) || 0;
  const cantidadFacturada = (item) => Number(item.quantity ?? item.cantidad) || 0;

  const cambiarCantidad = (item, i) => (e) => {
    const tope = cantidadFacturada(item);
    let valor = Number(e.target.value);
    if (Number.isNaN(valor) || valor < 0) valor = 0;
    // Una nota de crédito no puede devolver más de lo que se facturó.
    if (tipo === 'credit_note' && valor > tope) valor = tope;
    setCantidades((prev) => ({ ...prev, [idDe(item, i)]: valor }));
  };

  const seleccionados = items
    .map((item, i) => ({ item, cantidad: Number(cantidades[idDe(item, i)]) || 0 }))
    .filter((x) => x.cantidad > 0);

  const totalNota = seleccionados.reduce(
    (suma, x) => suma + x.cantidad * precioDe(x.item), 0
  );

  const esCredito = tipo === 'credit_note';
  const puedeEmitir = motivo.trim().length > 0 && seleccionados.length > 0 && !emitiendo;

  const emitir = async () => {
    if (!puedeEmitir) return;
    setEmitiendo(true);
    setError(null);
    try {
      await onEmitir({
        invoiceId: invoice._id,
        tipo,
        motivo: motivo.trim(),
        restaurarStock: esCredito && restaurarStock,
        items: seleccionados.map(({ item, cantidad }) => ({
          product: item.product?._id || item.product,
          quantity: cantidad,
          price: precioDe(item),
          taxType: item.taxType,
          alicuota: item.alicuota
        }))
      });
      onClose();
    } catch (e) {
      setError(e.message || 'No se pudo emitir la nota.');
    } finally {
      setEmitiendo(false);
    }
  };

  const celda = { color: 'white', fontSize: '13px', borderColor: 'rgba(255,255,255,0.1)' };
  const cabecera = { ...celda, bgcolor: '#26262C', fontWeight: 700, fontSize: '12px' };

  return (
    <Dialog open={open} onClose={emitiendo ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: '#1B1B1F', color: 'white', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Nota de ajuste sobre {invoice?.number || ''}
        <IconButton onClick={onClose} disabled={emitiendo} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: '0.9rem' }}>
          {esCredito
            ? 'Una nota de crédito descuenta de una factura ya emitida: una devolución, un descuento posterior, un error a favor del cliente.'
            : 'Una nota de débito suma a una factura ya emitida: intereses, gastos, un ajuste a favor tuyo.'}
          {' '}Lleva su propio número y queda enlazada a la factura que ajusta.
        </DialogContentText>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="tipo-nota" sx={{ color: 'rgba(255,255,255,0.6)' }}>Tipo de nota</InputLabel>
            <Select
              labelId="tipo-nota"
              label="Tipo de nota"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              disabled={emitiendo}
              sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }}
            >
              <MenuItem value="credit_note">Nota de crédito</MenuItem>
              <MenuItem value="debit_note">Nota de débito</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={emitiendo}
            placeholder={esCredito ? 'Por ejemplo: devolución de dos unidades' : 'Por ejemplo: intereses por mora'}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                '&.Mui-focused fieldset': { borderColor: '#F5A623' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' }
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
          Indicá cuánto de cada renglón entra en la nota. Los que queden en cero no se incluyen.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cabecera}>Descripción</TableCell>
              <TableCell sx={cabecera} align="right">Facturado</TableCell>
              <TableCell sx={cabecera} align="right">Precio</TableCell>
              <TableCell sx={cabecera} align="right">En la nota</TableCell>
              <TableCell sx={cabecera} align="right">Subtotal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => {
              const cantidad = Number(cantidades[idDe(item, i)]) || 0;
              return (
                <TableRow key={idDe(item, i)}>
                  <TableCell sx={celda}>
                    {item.descripcion || item.product?.nombre || item.nombre || 'Ítem'}
                  </TableCell>
                  <TableCell sx={celda} align="right">{cantidadFacturada(item)}</TableCell>
                  <TableCell sx={celda} align="right">
                    {formatearMoneda(precioDe(item), invoice?.moneda)}
                  </TableCell>
                  <TableCell sx={celda} align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={cantidades[idDe(item, i)] ?? ''}
                      onChange={cambiarCantidad(item, i)}
                      disabled={emitiendo}
                      placeholder="0"
                      inputProps={{
                        min: 0,
                        max: esCredito ? cantidadFacturada(item) : undefined,
                        style: { textAlign: 'right', color: 'white', padding: '6px 8px' }
                      }}
                      sx={{
                        width: 90,
                        '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.25)' }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...celda, fontVariantNumeric: 'tabular-nums' }} align="right">
                    {formatearMoneda(cantidad * precioDe(item), invoice?.moneda)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          {esCredito ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={restaurarStock}
                  onChange={(e) => setRestaurarStock(e.target.checked)}
                  disabled={emitiendo}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#F5A623' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  Devolver la mercadería al inventario
                </Typography>
              }
            />
          ) : <span />}
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Total de la nota: {formatearMoneda(totalNota, invoice?.moneda)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined" disabled={emitiendo}
          sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button onClick={emitir} variant="contained" disabled={!puedeEmitir}
          startIcon={emitiendo ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ bgcolor: '#F5A623', color: '#1C1525', fontWeight: 700, '&:hover': { bgcolor: '#E99A18' } }}>
          {emitiendo ? 'Emitiendo…' : `Emitir ${esCredito ? 'nota de crédito' : 'nota de débito'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotaAjusteDialog;
